import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { StorageService } from '../services/storage.service';

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private tokenEndpoint = `${environment.auth.backOfficeUrl}${environment.auth.authContextPath}/oauth/token`;
  private mockAuthenticated = false;
  private mockUsername = '';

  private accessToken: string | null = null;
  private refreshToken: string | null = null;
  private expirationDate: number | null = null; // Unix timestamp in seconds

  constructor(
    private http: HttpClient,
    private storageService: StorageService,
    private router: Router
  ) {}

  async loginDirect(username: string, password: string): Promise<void> {
    if (environment.mockMode) {
      if (!username || !password) throw { status: 400 };
      this.mockAuthenticated = true;
      this.mockUsername = username;
      return;
    }

    const body = new URLSearchParams();
    body.set('grant_type', 'password');
    body.set('username', username);
    body.set('password', password);

    const response = await firstValueFrom(
      this.http.post<TokenResponse>(this.tokenEndpoint, body.toString(), {
        headers: this.authHeaders(),
      })
    );

    this.storeTokensInMemory(response);
    await this.storageService.saveTokens({
      access_token: response.access_token,
      refresh_token: response.refresh_token,
    });
  }

  async tryRestoreSession(): Promise<boolean> {
    if (environment.mockMode) {
      return this.mockAuthenticated;
    }

    await this.storageService.init();
    const tokens = await this.storageService.getTokens();
    if (!tokens?.refresh_token) return false;

    try {
      const body = new URLSearchParams();
      body.set('grant_type', 'refresh_token');
      body.set('refresh_token', tokens.refresh_token);

      const response = await firstValueFrom(
        this.http.post<TokenResponse>(this.tokenEndpoint, body.toString(), {
          headers: this.authHeaders(),
        })
      );

      this.storeTokensInMemory(response);
      await this.storageService.saveTokens({
        access_token: response.access_token,
        refresh_token: response.refresh_token,
      });
      return true;
    } catch {
      await this.storageService.clearTokens();
      return false;
    }
  }

  get isLoggedIn(): boolean {
    if (environment.mockMode) return this.mockAuthenticated;
    return !!this.accessToken && !!this.expirationDate && this.expirationDate > Math.trunc(Date.now() / 1000);
  }

  async getToken(): Promise<string> {
    if (environment.mockMode) return 'mock-token';

    // If token is still valid (with 30s margin), return it
    if (this.accessToken && this.expirationDate && this.expirationDate > Math.trunc(Date.now() / 1000) + 30) {
      return this.accessToken;
    }

    // Token expired or about to expire — refresh
    if (this.refreshToken) {
      const body = new URLSearchParams();
      body.set('grant_type', 'refresh_token');
      body.set('refresh_token', this.refreshToken);

      const response = await firstValueFrom(
        this.http.post<TokenResponse>(this.tokenEndpoint, body.toString(), {
          headers: this.authHeaders(),
        })
      );

      this.storeTokensInMemory(response);
      await this.storageService.saveTokens({
        access_token: response.access_token,
        refresh_token: response.refresh_token,
      });
      return response.access_token;
    }

    throw new Error('No valid token available');
  }

  getUsername(): string {
    if (environment.mockMode) return this.mockUsername;
    if (!this.accessToken) return '';
    const payload = this.decodeJwt(this.accessToken);
    return payload['preferred_username'] || payload['sub'] || '';
  }

  async logout(): Promise<void> {
    if (environment.mockMode) {
      this.mockAuthenticated = false;
      this.mockUsername = '';
      await this.router.navigate(['/login'], { replaceUrl: true });
      return;
    }
    await this.storageService.clearTokens();
    this.accessToken = null;
    this.refreshToken = null;
    this.expirationDate = null;
    await this.router.navigate(['/login'], { replaceUrl: true });
  }

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'Accept': 'application/json',
      'Authorization': 'Basic ' + btoa(environment.auth.clientId + ':' + environment.auth.clientSecret),
      'X-Skip-Interceptor': '',
    });
  }

  private storeTokensInMemory(response: TokenResponse): void {
    this.accessToken = response.access_token;
    this.refreshToken = response.refresh_token;
    this.expirationDate = Math.trunc(Date.now() / 1000) + response.expires_in;
  }

  private decodeJwt(token: string): Record<string, any> {
    const payload = token.split('.')[1];
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  }
}
